// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Brim — the motion input layer (principle #23). One interface, three implementations,
// all producing the SAME thing the core consumes: a motion-energy number per tick.
//   · sensor    — DeviceMotion → MotionMeter (the accelerometer path, phones).
//   · null      — always 0 (no sensor / denied / desktop): the timing-gamble fallback,
//                 a real game where only OVERFLOW can bite. Built first; never a wall.
//   · synthetic — a settable value for the browser test hook, to prove sensor → spill.
// The sensor is an INPUT, never a gate. Permission is asked from a tap (iOS 13+). The
// sensor exposes only acceleration deltas — no stream, nothing to record, store or send;
// teardown just removes the listener.

import { MotionMeter } from './motion';

export type SourceKind = 'sensor' | 'null' | 'synthetic';

export interface MotionSource {
  readonly kind: SourceKind;
  /** Advance by `dtMs` and return the current smoothed motion energy (0 = dead still). */
  energy(dtMs: number): number;
  /** Zero the follower — a fresh orb. */
  reset(): void;
  dispose(): void;
}

/* ── DeviceMotion feature-detect + permission (iOS 13+) ─────────────────────── */

interface DMEStatic {
  requestPermission?: () => Promise<'granted' | 'denied'>;
}

export function motionExists(): boolean {
  return typeof window !== 'undefined' && 'DeviceMotionEvent' in window;
}

export function motionNeedsPermission(): boolean {
  return (
    motionExists() &&
    typeof (window.DeviceMotionEvent as unknown as DMEStatic).requestPermission === 'function'
  );
}

/** Must be called from within a user gesture (a tap). Resolves the permission state. */
export async function requestMotionPermission(): Promise<'granted' | 'denied' | 'unsupported'> {
  if (!motionExists()) return 'unsupported';
  const dme = window.DeviceMotionEvent as unknown as DMEStatic;
  if (typeof dme.requestPermission !== 'function') return 'granted'; // Android/desktop: no gate
  try {
    return await dme.requestPermission();
  } catch {
    return 'denied';
  }
}

/* ── the accelerometer source ───────────────────────────────────────────────── */

export interface SensorSource extends MotionSource {
  kind: 'sensor';
  /** Resolves true once a real (non-null) acceleration reading has arrived. */
  ready(timeoutMs: number): Promise<boolean>;
}

export function createSensorSource(): SensorSource {
  const meter = new MotionMeter();
  let gotData = false;
  const waiters: Array<() => void> = [];

  const onMotion = (e: DeviceMotionEvent): void => {
    const a = e.accelerationIncludingGravity ?? e.acceleration;
    if (!a || a.x === null || a.y === null || a.z === null) return;
    meter.push({ x: a.x ?? 0, y: a.y ?? 0, z: a.z ?? 0 });
    if (!gotData) {
      gotData = true;
      while (waiters.length) waiters.shift()!();
    }
  };
  window.addEventListener('devicemotion', onMotion);

  return {
    kind: 'sensor',
    energy(dtMs: number): number {
      meter.decay(dtMs);
      return meter.value();
    },
    reset() {
      meter.reset();
    },
    ready(timeoutMs: number) {
      if (gotData) return Promise.resolve(true);
      return new Promise<boolean>((resolve) => {
        const to = setTimeout(() => resolve(false), timeoutMs);
        waiters.push(() => {
          clearTimeout(to);
          resolve(true);
        });
      });
    },
    dispose() {
      window.removeEventListener('devicemotion', onMotion);
    },
  };
}

/* ── the fallback: no motion (timing gamble) ────────────────────────────────── */

export function createNullSource(): MotionSource {
  return {
    kind: 'null',
    energy: () => 0,
    reset() {},
    dispose() {},
  };
}

/* ── the browser test hook (synthetic sensor feed) ──────────────────────────── */

export interface SyntheticSource extends MotionSource {
  kind: 'synthetic';
  /** Set the motion energy the next energy() calls will report. */
  set(e: number): void;
}

export function createSyntheticSource(): SyntheticSource {
  let e = 0;
  return {
    kind: 'synthetic',
    energy: () => e,
    set(v: number) {
      e = Math.max(0, v);
    },
    reset() {
      e = 0;
    },
    dispose() {},
  };
}
